import { useCallback } from 'react'
import type { CheckoutStep } from '../types'
import {
  canNavigateToStep,
  resolveBackStep,
  resolveNextStepFromSession,
} from './CheckoutFlow.types'
import { pushCheckoutStepInUrl } from './checkoutUrlState'
import { resolveSubmitSession } from './useCheckoutSubmitHandlers/session'
import {
  submitBillingAddress,
  submitCheckoutConfirmation,
  submitShippingAddress,
  submitShippingMethod,
} from './useCheckoutSubmitHandlers/submissions'
import type {
  UseCheckoutSubmitHandlersInput,
  UseCheckoutSubmitHandlersOutput,
} from './useCheckoutSubmitHandlers/types'

export type { UseCheckoutSubmitHandlersOutput }

export const useCheckoutSubmitHandlers = ({
  state,
  dispatch,
  token,
  toLocalizedPath,
  t,
  onCheckoutCompleted,
}: UseCheckoutSubmitHandlersInput): UseCheckoutSubmitHandlersOutput => {
  const onNavigateToStep = useCallback((step: CheckoutStep) => {
    if (!state.session) return
    if (!canNavigateToStep(state.session, step)) {
      dispatch({ type: 'SET_ERROR', code: 'invalid_step' })
      return
    }
    pushCheckoutStepInUrl(step)
    dispatch({ type: 'SET_STEP', step })
    dispatch({ type: 'CLEAR_VALIDATION_ERRORS' })
  }, [dispatch, state.session])

  const onContinueFromCartReview = useCallback(() => {
    if (!state.session) return
    onNavigateToStep(resolveNextStepFromSession(state.session))
  }, [onNavigateToStep, state.session])

  const onBackStep = useCallback(() => {
    const target = resolveBackStep({
      step: state.step,
      sameAsShipping: state.sameAsShipping,
    })
    if (!target) return
    onNavigateToStep(target)
  }, [onNavigateToStep, state.sameAsShipping, state.step])

  const onRetryBootstrap = useCallback(() => {
    dispatch({ type: 'RETRY_BOOTSTRAP' })
  }, [dispatch])

  const onSubmitShippingAddress = useCallback(async () => {
    const activeSession = resolveSubmitSession({ state, dispatch })
    if (!activeSession) return
    await submitShippingAddress({ state, dispatch, token, t })
  }, [dispatch, state, t, token])

  const onSubmitBillingAddress = useCallback(async () => {
    const activeSession = resolveSubmitSession({ state, dispatch })
    if (!activeSession) return
    await submitBillingAddress({ state, dispatch, token, t })
  }, [dispatch, state, t, token])

  const onSubmitShippingMethod = useCallback(async () => {
    const activeSession = resolveSubmitSession({ state, dispatch })
    if (!activeSession) return
    await submitShippingMethod({ state, dispatch, token })
  }, [dispatch, state, token])

  const onConfirmCheckout = useCallback(async () => {
    const activeSession = resolveSubmitSession({ state, dispatch })
    if (!activeSession) return
    await submitCheckoutConfirmation({
      state,
      dispatch,
      token,
      toLocalizedPath,
      onCheckoutCompleted,
    })
  }, [dispatch, onCheckoutCompleted, state, toLocalizedPath, token])

  return {
    onContinueFromCartReview,
    onBackStep,
    onNavigateToStep,
    onSubmitShippingAddress,
    onSubmitBillingAddress,
    onSubmitShippingMethod,
    onConfirmCheckout,
    onRetryBootstrap,
  }
}
